import type { FlexProps } from '@chakra-ui/react';
import { Flex } from '@chakra-ui/react';
import type { Msg } from '@halo/chat-sdk-react';
import { MessageAction, StickerSetType } from '@halo/chat-sdk-react';
import type {
  EmojiProps,
  MessageTextAreaProps,
  MessageTextAreaRef,
  SerializePayloadResult,
} from '@halo/ui-chat-react';
import {
  EditorContext,
  EmojiButton,
  formatEmojiId,
  MessageTextArea,
  useEditorContext,
} from '@halo/ui-chat-react';
import type { RefObject } from 'react';
import { useStaticAppStore } from '@/store/client';

export interface ForwardComposerInputRef {
  send: (params: { sessionIds: string[]; forwardMsg?: Msg }) => Promise<void>;
}

export type ForwardComposerInputProps = FlexProps &
  Pick<MessageTextAreaProps, 'onValidChange'> & {
    refComponent: RefObject<ForwardComposerInputRef | null>;
  };

export const ForwardComposerInput = memo(function ForwardComposerInput(
  props: ForwardComposerInputProps
) {
  return (
    <EditorContext>
      <ForwardComposerInputMain {...props}></ForwardComposerInputMain>
    </EditorContext>
  );
});

function ForwardComposerInputMain({
  onValidChange,
  refComponent,
  ...props
}: ForwardComposerInputProps) {
  const { client } = useStaticAppStore();
  const { editor } = useEditorContext();
  const textareaRef = useRef<MessageTextAreaRef>(null);
  const { t } = useTranslation();

  const taskMapRef = useRef<Record<string, Promise<void>>>({});
  const onSelectEmoji = useCallback<EmojiProps['onSelect']>(
    (emojiId, _groupId, type) => {
      if (type === StickerSetType.Emoji) {
        editor.insertText(formatEmojiId(emojiId));
        editor.focus();
      }
    },
    [editor]
  );

  const send = useCallback<ForwardComposerInputRef['send']>(
    async ({ sessionIds, forwardMsg }) => {
      if (!client) return;
      taskMapRef.current = sessionIds.reduce(
        (map, chat_id) => {
          map[chat_id] = forwardMsg
            ? client.Api.messages.sendForwardMsg({
                chat_id,
                forwardUid: forwardMsg.msg_ext?.forwardUid || forwardMsg.owner_id,
                payload: forwardMsg.payload,
                payload_type: forwardMsg.payload_type,
                action: MessageAction.FORWARD_ENCRYPT,
              })
            : Promise.resolve();
          return map;
        },
        {} as Record<string, Promise<void>>
      );
      await textareaRef.current?.send();
      await Promise.all(Object.values(taskMapRef.current));
    },
    [client]
  );

  const onSend = useCallback(
    (result: SerializePayloadResult) => {
      Object.entries(taskMapRef.current).forEach(([chat_id, task]) => {
        taskMapRef.current[chat_id] = task.then(() => {
          return client!.Api.messages.sendMsg({
            chat_id,
            payload: result.payload,
          });
        });
      });
    },
    [client]
  );

  useImperativeHandle(
    refComponent,
    () => ({
      send,
    }),
    [send]
  );

  return (
    <Flex
      className="ForwardComposerFooter"
      border="1px solid"
      borderColor="border.muted"
      borderBottomRadius="md"
      p="2"
      fontSize="sm"
      gap="1"
      {...props}
    >
      <EmojiButton onSelect={onSelectEmoji} mini={true} />
      <MessageTextArea
        ref={textareaRef}
        wrapperProps={{
          css: {
            '--slate-editor-max-height': '100px',
          },
        }}
        placeholder={t('chat.addCaption')}
        sendOnEnter={false}
        onSend={onSend}
        onValidChange={onValidChange}
      />
    </Flex>
  );
}
